import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import styles from './HistoricDataGrid.module.scss';
import { TableHeader } from '../TableHeader';
import { Loading } from '../../components/Loading';
import { isEmpty, isEmptyArray, numberWithCommas, formatPercent, formatDollar } from '../../util/functions';
import { getData, downloadData } from '../../api/dashboard';
import DataGrid, { Column, Scrolling, Summary, TotalItem, ColumnFixing, Export } from 'devextreme-react/data-grid';
import { COLUMN_HEIGHT } from '../../util/dashBoardPageConstants';

var noSort = {
  mode: 'none'
};
var noPaging = {
  enabled: false
};

var loadingOptions = {
  enabled: false
};

const formatNumber = {
  type: 'fixedPoint',
  precision: 0
};

const formatNotional = {
  type: 'currency',
  precision: 0
};

const addPercentToData = (data) => {
  if (data.value !== null) {
    return formatPercent(data.value);
  }
  return data.value;
};

class HistoricDataGrid extends React.Component {
  constructor (props) {
    super(props);
    this.dataGridRef = React.createRef();
    this.state = {
      lastUpdateTime: null,
      data: {},
      isLoaded: false,
      isDownloadInactive: false
    };
    this.populateGridData = this.populateGridData.bind(this);
    this.getGridDataAPI = this.getGridDataAPI.bind(this);
    this.download = this.download.bind(this);
    this.renderColumns = this.renderColumns.bind(this);
    this.customizeTotal = this.customizeTotal.bind(this);
    this.customizeColumnTotal = this.customizeColumnTotal.bind(this);
    this.customizeSummaryTitle = this.customizeSummaryTitle.bind(this);
    this.handleErrorResults = this.handleErrorResults.bind(this);
  }

  get dataGrid () {
    var gridRef;
    if (this.dataGridRef && this.dataGridRef.current) {
      gridRef = this.dataGridRef.current.instance;
    }
    return gridRef;
  }

  componentDidMount () {
    // get data from the props
    this.getGridDataAPI();
  }

  componentDidUpdate (prevProps) {
    const historicDate = this.props.historicDate;
    if (historicDate && prevProps.historicDate !== historicDate) {
      if (historicDate) {
        this.setState(prevState => ({
          isLoaded: false
        }), this.getGridDataAPI);
      }
    }
  }

  getGridDataAPI () {
    let api = this.props.api;
    const historicDate = this.props.historicDate;
    if (!isEmpty(api && historicDate)) {
      api = `${api}?date=${historicDate}`;
      getData(api)
        .then(data => {
          this.populateGridData(data);
        })
        .catch((error) => {
          console.log(error);
          var message = (error && error.response &&
            error.response.data && error.response.data) || 'Data currently unavailable';
          if (message && (message.includes('504') || message.includes('Gateway'))) {
            message = 'Query Timed out , Please adjust your filter criteria to return a smaller result set.';
          }
          this.handleErrorResults(message);
        });
    }
  }

  populateGridData (data) {
    const lastUpdatedField = this.props.lastUpdatedField;
    const totalField = this.props.totalField;
    if (isEmptyArray(data)) {
      data = [];
    }
    const lastUpdateTime = data && data[0] && data[0][lastUpdatedField] && moment(data[0][lastUpdatedField], 'YYYY-MM-DD HH:mm:ss.SSS').format('MM/DD/YY hh:mm:ss A');
    const totalCount = (data && data[0] && data[0][totalField] && numberWithCommas(data[0][totalField])) || '';
    this.setState({
      data: data,
      error: null,
      isLoaded: true,
      lastUpdateTime: lastUpdateTime,
      totalCount: totalCount
    });
  }

  download () {
    if (this.state.isDownloadInactive) return;
    // make it inactive for future request until current one is solved
    this.setState({ isDownloadInactive: true });
    // download  data
    let api = this.props.downloadApi;
    if (!isEmpty(api)) {
      const header = this.props.identifier && this.props.identifier.toUpperCase().replaceAll('-', '_');
      const historicDate = this.props.historicDate;
      const name = `${header}-${historicDate}`;
      api = `${api}?date=${historicDate}`;
      downloadData(api, name).then(data => {
        this.setState({ isDownloadInactive: false });
      })
        .catch((err) => {
        // error
          this.setState({ isDownloadInactive: false });
          console.log(err);
        });
    } else {
      if (this.dataGrid) {
        this.dataGrid.exportToExcel(false);
      }
      this.setState({ isDownloadInactive: false });
    }
  }

  handleErrorResults (errorMessage) {
    this.setState(prevState => ({
      isLoaded: true,
      data: {},
      error: errorMessage
    }));
  }

  renderColumns () {
    return this.props.columns && this.props.columns.map(function (item, index) {
      if (item.type === 'number') {
        return <Column height={COLUMN_HEIGHT} format={formatNumber} type='number' key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.type === 'notional') {
        return <Column height={COLUMN_HEIGHT} format={formatNotional} type='number' key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else if (item.type === 'percent') {
        return <Column height={COLUMN_HEIGHT} customizeText={addPercentToData} key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      } else {
        return <Column height={COLUMN_HEIGHT} width='55px' key={index + item.caption} dataField={item.dataField} caption={item.caption} />;
      }
    });
  }

  renderSummary () {
    const summaryGroupTitleColumn = this.props.summaryGroupTitleColumn;
    const summaryGroupDataColumn = this.props.summaryGroupDataColumn;
    const summaryGroupDataCustomColumns = this.props.summaryGroupDataCustomColumns;
    const totalField = this.props.totalField;
    if (summaryGroupTitleColumn && summaryGroupDataColumn && totalField) {
      return <Summary >
        <TotalItem
          column={summaryGroupTitleColumn.dataField}
          summaryType='count'
          customizeText={this.customizeSummaryTitle} />
        <TotalItem
          column={summaryGroupDataColumn.dataField}
          summaryType='count'
          cssClass={'dashboardGridSummaryTotalNumber'}
          customizeText={this.customizeTotal} />
      </Summary>;
    } else if (summaryGroupTitleColumn && summaryGroupDataCustomColumns) {
      const _this = this;
      return <Summary >
        <TotalItem
          column={summaryGroupTitleColumn.dataField}
          summaryType='count'
          customizeText={this.customizeSummaryTitle} />

        {summaryGroupDataCustomColumns.map(function (item, index) {
          return <TotalItem
            key={`Summary${index}`}
            column={item.dataField}
            summaryType='count'
            cssClass={'dashboardGridSummaryTotalNumber'}
            customizeText={() => _this.customizeColumnTotal(item.totalField, item.type)} />;
        })}
      </Summary>;
    } else {
      return <div />;
    }
  }

  customizeTotal (data) {
    return `${this.state.totalCount}`;
  }

  customizeColumnTotal (field, type) {
    const data = this.state.data;
    let totalCount;
    if (type === 'notional') {
      totalCount = (data && data[0] && data[0][field] && formatDollar(data[0][field])) || '';
    } else {
      totalCount = (data && data[0] && data[0][field] && numberWithCommas(data[0][field])) || '';
    }
    return totalCount;
  }

  customizeSummaryTitle () {
    return `Total`;
  }

  render () {
    const header = this.props.header;
    const historicDate = this.props.historicDate;
    const downloadHeader = this.props.identifier && this.props.identifier.toUpperCase().replaceAll('-', '_');
    const downloadFilename = `${downloadHeader}-${historicDate}`;
    const data = this.state.data;
    return (
      <div>
        {historicDate && <div className={classNames(styles.container)}>

          <TableHeader headerUpperCase={false}
            header={header}
            className={styles.tableHeader}
            download={this.download}
            downloadIconId={this.props.identifier.concat('-download')}
            tableHeaderMargin={false}
            topBorderOnly={false}
            bottomBorderOnly />
          <Loading
            type='table'
            data={data}
            loaded={this.state.isLoaded}
            reloading={false}
            error={this.state.error}
            emptyMessage='No results found.' >
            <DataGrid
              ref={this.dataGridRef}
              id={this.props.identifier}
              dataSource={data}
              columnAutoWidth
              showColumnLines
              showRowLines
              rowAlternationEnabled
              showBorders
              repaintChangesOnly
              paging={noPaging}
              sorting={noSort}
              loadPanel={loadingOptions}
              height={this.props.maxRows ? (this.props.maxRows + 1) * COLUMN_HEIGHT : 450}
              cacheEnabled={false}
            >
              {this.renderColumns()}

              <Scrolling mode='virtual' />
              <Export enabled fileName={downloadFilename} />
              {this.renderSummary()}
              <ColumnFixing enabled />
            </DataGrid>
            <div id='lastUpdated' className={styles.textNote} >Update: <span className={styles.lastUpdated}>{this.state.lastUpdateTime}</span></div>
          </Loading>
        </div>}
      </div>

    );
  }
}

HistoricDataGrid.propTypes = {
  api: PropTypes.string.isRequired,
  downloadApi: PropTypes.string,
  historicDate: PropTypes.string,
  header: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  lastUpdatedField: PropTypes.string.isRequired,
  totalField: PropTypes.string,
  identifier: PropTypes.string.isRequired,
  summaryGroupTitleColumn: PropTypes.object.isRequired,
  summaryGroupDataColumn: PropTypes.object,
  summaryGroupDataCustomColumns: PropTypes.array,
  maxRows: PropTypes.number
};

export default HistoricDataGrid;
